import React, { useState } from 'react';
import { Copy, Calendar, Trash2, Loader2 } from 'lucide-react';
import { SmartCalendar } from '@/apps/master-plan/shared/components/SmartCalendar';
import { EventManager } from '@/apps/master-plan/services/event-manager.service';
import { toLogicDate } from '@/utils/date-utils';
import { useComposerStore } from '@stores/v4/useComposerStore';

interface ComposerActionsProps {
    event: any;
}

export const ComposerActions: React.FC<ComposerActionsProps> = ({ event }) => {
    const [pickerMode, setPickerMode] = useState<'CLONE' | 'RESCHEDULE' | null>(null);
    const [cloneMonth, setCloneMonth] = useState<Date>(new Date());
    const [isProcessing, setIsProcessing] = useState(false);
    const { closeComposer, deleteComposedEvent } = useComposerStore();

    if (!event || !event.id) return null;

    const handleClone = async (date: Date) => {
        setIsProcessing(true);
        try {
            await EventManager.cloneEvent(event, date);
            setPickerMode(null);
            closeComposer();
        } catch (e: any) {
            alert("Clone failed: " + e.message);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleReschedule = async (date: Date) => {
        setIsProcessing(true);
        try {
            await EventManager.rescheduleEvent(event.id, toLogicDate(date));
            setPickerMode(null);
            closeComposer();
        } catch (e: any) {
            alert("Reschedule failed: " + e.message);
        } finally {
            setIsProcessing(false);
        }
    };

    interface CommandButtonProps {
        icon: any;
        label: string;
        color?: string;
        onClick: () => void;
    }

    const CommandButton: React.FC<CommandButtonProps> = ({ icon: Icon, label, color = "#f29b11", onClick }) => (
        <button
            onClick={onClick}
            style={{
                all: 'unset', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px',
                cursor: 'pointer', flexDirection: 'row-reverse', width: '100%',
                justifyContent: 'flex-start'
            }}
        >
            <div style={{
                width: '42px', height: '42px', borderRadius: '50%',
                backgroundColor: '#111', border: `2px solid ${color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 10px rgba(0,0,0,0.5)`, flexShrink: 0
            }}>
                <Icon size={18} color={color} />
            </div>
            <div style={{ textAlign: 'right', flex: 1 }}>
                <div style={{ fontSize: '12px', fontWeight: '900', color: '#fff', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    {label}
                </div>
            </div>
        </button>
    );

    return (
        <div style={{ padding: '20px', borderTop: '1px solid #222', marginTop: '20px' }}>
            {isProcessing && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                    <Loader2 size={24} color="#f29b11" className="animate-spin" />
                </div>
            )}

            {!pickerMode && !isProcessing && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    {event.status === 'PLANNED' && (
                        <CommandButton icon={Calendar} label="Reschedule" color="#3b82f6" onClick={() => setPickerMode('RESCHEDULE')} />
                    )}
                    <CommandButton icon={Copy} label="Clone" color="#2ecc71" onClick={() => setPickerMode('CLONE')} />
                    <CommandButton icon={Trash2} label="Delete" color="#ef4444" onClick={deleteComposedEvent} />
                </div>
            )}

            {pickerMode && !isProcessing && (
                <div className="animate-in fade-in zoom-in-95 duration-300" style={{ backgroundColor: 'rgba(10,10,10,0.9)', padding: '15px', borderRadius: '20px', border: '1px solid #2ecc7144', marginBottom: '15px' }}>
                    <h3 style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold', marginBottom: '15px', textAlign: 'center', textTransform: 'uppercase' }}>
                        {pickerMode === 'CLONE' ? 'Clone Event To' : 'Reschedule To'}
                    </h3>
                    <SmartCalendar 
                        currentMonth={cloneMonth}
                        onMonthChange={setCloneMonth}
                        selectedDate={new Date()}
                        onSelectDate={(date: Date) => {
                            if (pickerMode === 'CLONE') handleClone(date);
                            else handleReschedule(date);
                        }}
                        getDateStyle={() => ({ color: '#666', fontWeight: 500, opacity: 1, markers: [] })}
                    />
                    <button 
                        onClick={() => setPickerMode(null)}
                        style={{ width: '100%', padding: '12px', marginTop: '10px', backgroundColor: 'transparent', border: '1px solid #444', borderRadius: '10px', color: '#888', fontSize: '10px', fontWeight: '900', textTransform: 'uppercase' }}
                    >
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
};
